import { useState } from "react";
import { motion } from "framer-motion";
import { publicApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Sparkles, CheckCircle2, HeartHandshake } from "lucide-react";
import { SashaChat } from "@/components/SashaChat";

const fadeUp = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.45 },
};

const STEPS = [
  { n: "01", t: "Tell us about you", d: "Share your company, your properties and your booking links in the form below." },
  { n: "02", t: "We align the brand", d: "Sasha is set up in your voice — your company's tone, look and standards." },
  { n: "03", t: "She learns your properties", d: "Dining, activities, property features, house rules — everything a guest could ask." },
  { n: "04", t: "Your Sasha is delivered", d: "Ready to support your guests door-to-door. Your Sasha will be sent to you." },
];

const LEVELS = [
  {
    id: "sassy",
    name: "Sasha Sassy",
    setup: "$250 set up",
    monthly: "$200 / mo",
    blurb: "A Sasha subscription providing booking support for your guests when needed.",
    features: ["Where to dine & local activities", "Property features & house rules", "Guest questions, answered anytime", "Booking support on demand"],
  },
  {
    id: "sexy_sassy",
    name: "Sasha Sexy & Sassy",
    setup: "$500 set up",
    monthly: "$500 / mo",
    featured: true,
    blurb: "Your property-specific model with your company-aligned setup — pushing and co-hosting your properties only.",
    features: ["Everything in Sasha Sassy", "Your own property-specific model", "Company-aligned branding & voice", "Co-hosting your properties exclusively", "Same door-to-door services, just for your properties", "Daily activity reports"],
  },
];

const FEATURES = ["Custom look", "Custom voice", "Describe properties", "Add planning", "All the above"];

const initialForm = {
  full_name: "",
  email: "",
  company: "",
  company_description: "",
  property_descriptions: "",
  booking_links: "",
  typical_guests: "",
  guest_experience: "",
  sasha_features: [],
  level: "sassy",
};

export default function YourSasha() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleFeature = (feat) => {
    setForm((f) => {
      let next;
      if (feat === "All the above") {
        next = f.sasha_features.includes("All the above") ? [] : [...FEATURES];
      } else {
        next = f.sasha_features.includes(feat)
          ? f.sasha_features.filter((x) => x !== feat && x !== "All the above")
          : [...f.sasha_features.filter((x) => x !== "All the above"), feat];
        if (FEATURES.slice(0, 4).every((x) => next.includes(x))) next = [...FEATURES];
      }
      return { ...f, sasha_features: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.level !== "sexy_sassy") {
        payload.sasha_features = [];
      }
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await publicApi.post("/your-sasha", payload);
      setDone(true);
      toast.success("Received — your Sasha will be sent to you.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Please complete all fields.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="your-sasha-page" className="bg-[#FAF9F6]">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 lg:pt-24 pb-12">
        <motion.div {...fadeUp} className="max-w-3xl">
          <div className="eyebrow">Your Sasha</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 text-[#2A1F1A] leading-[1.05]">
            Build a Sasha <span className="text-[#C03028] italic">for your property.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#6E6E70] leading-relaxed">
            Janté works with owners, agencies, property managers and hospitality brands to create a
            Sasha with <em>their</em> branding — in a few short steps. Tell us about your company and
            your properties, and your Sasha will be sent to you.
          </p>
        </motion.div>
      </section>

      {/* Steps */}
      <section className="bg-[#F2EFE9] border-y border-[#E3DED5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid grid-cols-1 md:grid-cols-4 gap-8">
          {STEPS.map((s, i) => (
            <motion.div key={s.n} {...fadeUp} transition={{ duration: 0.45, delay: i * 0.08 }} data-testid={`sasha-step-${s.n}`}>
              <div className="font-serif-display text-4xl text-[#B0895C]">{s.n}</div>
              <h3 className="font-serif-display text-xl text-[#2A1F1A] mt-2">{s.t}</h3>
              <p className="text-sm text-[#6E6E70] mt-2 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Levels */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <motion.div {...fadeUp}>
          <div className="eyebrow">Levels</div>
          <h2 className="font-serif-display text-3xl text-[#2A1F1A] mt-2">Choose your Sasha</h2>
        </motion.div>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
          {LEVELS.map((lv, i) => (
            <motion.div
              key={lv.id}
              {...fadeUp}
              transition={{ duration: 0.45, delay: i * 0.1 }}
              data-testid={`level-card-${lv.id}`}
              className={`border p-8 bg-white flex flex-col ${lv.featured ? "border-[#C03028] shadow-[0_10px_40px_-18px_rgba(192,48,40,0.35)]" : "border-[#E3DED5]"}`}
            >
              {lv.featured && (
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#C03028] font-semibold mb-3 inline-flex items-center gap-1.5">
                  <Sparkles size={11} /> Property-specific model
                </div>
              )}
              <h3 className="font-serif-display text-2xl text-[#2A1F1A]">{lv.name}</h3>
              <div className="mt-3 flex items-baseline gap-3">
                <span className="font-serif-display text-3xl text-[#2A1F1A]">{lv.monthly}</span>
                <span className="text-sm text-[#6E6E70]">{lv.setup}</span>
              </div>
              <p className="mt-4 text-sm text-[#6E6E70] leading-relaxed">{lv.blurb}</p>
              <ul className="mt-5 space-y-2.5 flex-1">
                {lv.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#2A1F1A]">
                    <CheckCircle2 size={14} className="text-[#B0895C] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                data-testid={`level-select-${lv.id}`}
                onClick={() => {
                  setForm((f) => ({ ...f, level: lv.id }));
                  document.getElementById("sasha-form")?.scrollIntoView({ behavior: "smooth" });
                }}
                className={`mt-7 px-5 py-2.5 text-sm tracking-wide ${lv.featured ? "btn-primary" : "btn-secondary"}`}
              >
                Start with {lv.name}
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section id="sasha-form" className="bg-[#F2EFE9] border-y border-[#E3DED5]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
          <motion.div {...fadeUp}>
            <div className="eyebrow">Request your Sasha</div>
            <h2 className="font-serif-display text-3xl text-[#2A1F1A] mt-2">A few short steps</h2>
          </motion.div>
          {done ? (
            <div data-testid="sasha-form-success" className="mt-8 bg-white border border-[#E3DED5] p-8 text-center">
              <CheckCircle2 size={32} className="text-[#2D4C3E] mx-auto" />
              <h3 className="font-serif-display text-2xl text-[#2A1F1A] mt-4">Received.</h3>
              <p className="text-sm text-[#6E6E70] mt-2">
                We&apos;re aligning Sasha to your brand — your Sasha will be sent to you.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 bg-white border border-[#E3DED5] p-6 lg:p-8 space-y-4" data-testid="your-sasha-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="eyebrow">Your name</Label>
                  <Input data-testid="ys-name" required value={form.full_name} onChange={set("full_name")} className="editorial-input mt-2" placeholder="Eleza F." />
                </div>
                <div>
                  <Label className="eyebrow">Email</Label>
                  <Input data-testid="ys-email" required type="email" value={form.email} onChange={set("email")} className="editorial-input mt-2" placeholder="you@company.com" />
                </div>
              </div>

              <div>
                <Label className="eyebrow">Level</Label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {LEVELS.map((lv) => (
                    <label
                      key={lv.id}
                      data-testid={`ys-level-${lv.id}`}
                      className={`border p-4 cursor-pointer text-sm ${form.level === lv.id ? "border-[#C03028] bg-[#FDF6F5]" : "border-[#E3DED5]"}`}
                    >
                      <input type="radio" name="level" value={lv.id} checked={form.level === lv.id} onChange={set("level")} className="sr-only" />
                      <div className="font-semibold text-[#2A1F1A]">{lv.name}</div>
                      <div className="text-[#6E6E70] text-xs mt-1">{lv.setup} · {lv.monthly}</div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Get to know your property */}
              <div className="border-t border-[#E3DED5] pt-5" data-testid="ys-property-section">
                <div className="eyebrow">Get to know your property</div>
                <p className="text-xs text-[#6E6E70] mt-1">To better understand your guests.</p>
                <div className="mt-4 space-y-4">
                  <div>
                    <Label className="eyebrow">Property descriptions</Label>
                    <Textarea data-testid="ys-property-desc" required value={form.property_descriptions} onChange={set("property_descriptions")} className="editorial-input mt-2 min-h-[80px]" placeholder="Your properties — features, rules, neighborhoods, what makes them special…" />
                  </div>
                  <div>
                    <Label className="eyebrow">Property booking links</Label>
                    <Textarea data-testid="ys-booking-links" required value={form.booking_links} onChange={set("booking_links")} className="editorial-input mt-2 min-h-[60px]" placeholder="Airbnb / VRBO / direct booking URLs, one per line" />
                  </div>
                  <div>
                    <Label className="eyebrow">Who are your typical guests?</Label>
                    <Textarea data-testid="ys-typical-guests" value={form.typical_guests} onChange={set("typical_guests")} className="editorial-input mt-2 min-h-[60px]" placeholder="Families, couples, business travelers, wine-country weekenders…" />
                  </div>
                  <div>
                    <Label className="eyebrow">What should Sasha know to delight them?</Label>
                    <Textarea data-testid="ys-guest-experience" value={form.guest_experience} onChange={set("guest_experience")} className="editorial-input mt-2 min-h-[60px]" placeholder="House quirks, favorite local spots, arrival tips, the little touches you're known for…" />
                  </div>
                </div>
              </div>

              {/* Know your company — Sasha Sexy & Sassy only */}
              {form.level === "sexy_sassy" && (
                <div className="border-t border-[#E3DED5] pt-5" data-testid="ys-company-section">
                  <div className="eyebrow text-[#C03028]">Know your company</div>
                  <p className="text-xs text-[#6E6E70] mt-1">For Sasha Sexy &amp; Sassy — so your Sasha is truly yours.</p>
                  <div className="mt-4 space-y-4">
                    <div>
                      <Label className="eyebrow">Company</Label>
                      <Input data-testid="ys-company" required value={form.company} onChange={set("company")} className="editorial-input mt-2" placeholder="Your company or brand name" />
                    </div>
                    <div>
                      <Label className="eyebrow">Company description</Label>
                      <Textarea data-testid="ys-company-desc" required value={form.company_description} onChange={set("company_description")} className="editorial-input mt-2 min-h-[80px]" placeholder="Who you are, your brand voice, the guest experience you deliver…" />
                    </div>
                    <div>
                      <Label className="eyebrow">What features do you want your Sasha to have?</Label>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {FEATURES.map((feat) => (
                          <label
                            key={feat}
                            data-testid={`ys-feature-${feat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className={`border px-4 py-3 cursor-pointer text-sm flex items-center gap-3 ${form.sasha_features.includes(feat) ? "border-[#C03028] bg-[#FDF6F5]" : "border-[#E3DED5]"}`}
                          >
                            <input
                              type="checkbox"
                              checked={form.sasha_features.includes(feat)}
                              onChange={() => toggleFeature(feat)}
                              className="accent-[#C03028]"
                            />
                            <span className="text-[#2A1F1A]">{feat}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <button type="submit" disabled={submitting} data-testid="ys-submit-btn" className="btn-primary w-full py-3 text-sm tracking-wide inline-flex items-center justify-center gap-2">
                {submitting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                {submitting ? "Sending…" : "Send me my Sasha"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Mission */}
      <section data-testid="mission-section" className="max-w-7xl mx-auto px-6 md:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div {...fadeUp}>
            <div className="eyebrow">The Mission</div>
            <h2 className="font-serif-display text-3xl lg:text-4xl text-[#2A1F1A] mt-3 leading-tight">
              Eliminate homelessness — <span className="text-[#C03028] italic">one stay at a time.</span>
            </h2>
            <p className="mt-5 text-base text-[#6E6E70] leading-relaxed">
              Every Janté property carries the mission. Hosts can partner with, publicize, and brand
              themselves by giving back to the communities they operate in — matching the Janté.
            </p>
            <p className="mt-4 text-base text-[#6E6E70] leading-relaxed">
              It&apos;s fully tax deductible: give to communities instead of taxes.
            </p>
          </motion.div>
          <motion.div {...fadeUp} transition={{ duration: 0.45, delay: 0.1 }} className="space-y-4">
            <div className="border border-[#E3DED5] bg-white p-6 flex items-start gap-4" data-testid="mission-jante-give">
              <div className="font-serif-display text-4xl text-[#C03028]">12.5%</div>
              <div>
                <div className="font-semibold text-[#2A1F1A]">Janté gives</div>
                <p className="text-sm text-[#6E6E70] mt-1">12.5% of net revenues to the mission — housing families in need.</p>
              </div>
            </div>
            <div className="border border-[#E3DED5] bg-white p-6 flex items-start gap-4" data-testid="mission-host-give">
              <div className="font-serif-display text-4xl text-[#B0895C]">12.5%</div>
              <div>
                <div className="font-semibold text-[#2A1F1A]">Hosts match</div>
                <p className="text-sm text-[#6E6E70] mt-1">12.5% of net revenues to the charity of your choice — fully tax deductible.</p>
              </div>
            </div>
            <div className="border border-[#E3DED5] bg-[#F2EFE9] p-6 flex items-start gap-4">
              <HeartHandshake size={28} className="text-[#C03028] shrink-0 mt-1" />
              <p className="text-sm text-[#2A1F1A] leading-relaxed">
                Together, 25% of net revenues flow back into communities. Partner with the mission,
                publicize it, and make giving part of your brand.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sasha hosts this page */}
      <SashaChat />
    </main>
  );
}
